import React, { useRef, useMemo, useCallback } from 'react';
import { useIntl } from '@atlassian/jira-intl';
import messages from '../../common/messages';
import type { ReadViewContainerProps, ReadViewEditButtonProps, ReadViewProps } from './types';

const DRAG_THRESHOLD = 5;

const isLinkOrButton = (element: HTMLElement): boolean =>
	element.closest('a') != null || element.closest('button') != null;

const ReadView = ({
	editButtonLabel,
	editButtonRef,
	onEdit,
	postReadViewClick,
	readView,
}: ReadViewProps) => {
	const startX = useRef(0);
	const startY = useRef(0);
	const { formatMessage } = useIntl();

	const mouseHasMovedAfterMouseDown = useCallback(
		(event: { clientX: number; clientY: number }) =>
			Math.abs(startX.current - event.clientX) >= DRAG_THRESHOLD ||
			Math.abs(startY.current - event.clientY) >= DRAG_THRESHOLD,
		[],
	);

	const onReadViewClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const element = event.target as HTMLElement;
			/** If a link or button is clicked in the read view, default action should be taken */
			if (!isLinkOrButton(element) && !mouseHasMovedAfterMouseDown(event)) {
				event.preventDefault();
				onEdit();
				postReadViewClick();
			}
		},
		[mouseHasMovedAfterMouseDown, onEdit, postReadViewClick],
	);

	const containerProps: ReadViewContainerProps = useMemo(
		() => ({
			/**
			 * It is not normally acceptable to add click handlers to non-interactive elements
			 * as this is an accessibility anti-pattern. However, because this instance is
			 * account for clicking on links that may be embedded within inline-edit and not
			 * creating an inaccessible custom element, we can add role="presentation" so that
			 * there is no negative impacts to assistive technologies.
			 * (Why links are embedded in inline-edit is for another day...)
			 */
			role: 'presentation',
			onClick: onReadViewClick,
			onMouseDown: (e) => {
				startX.current = e.clientX;
				startY.current = e.clientY;
			},
		}),
		[onReadViewClick],
	);

	const editButtonProps: ReadViewEditButtonProps = useMemo(
		() => ({
			label: editButtonLabel ?? formatMessage(messages.editButtonLabel),
			type: 'button',
			onClick: onEdit,
			ref: editButtonRef,
		}),
		[editButtonLabel, editButtonRef, formatMessage, onEdit],
	);

	return <>{readView({ containerProps, editButtonProps })}</>;
};

export default ReadView;
